import { ListenableSubscribable } from '@benzinga/subscribable';
import { GptSearchEvent } from './manager';
import { GptResponseData, Prompt } from './entity';
import { deepObjectMerge, isNilOrEmpty, objectDeepDiff } from '@benzinga/utils';

type CachedConfigWithWidgetId<T extends object = object> = GptResponseData<T> & { widgetId: string };

export class GptSearchStore<T extends object = object> extends ListenableSubscribable<GptSearchEvent> {
  private cachedConfigs: CachedConfigWithWidgetId[];
  private promptsList: Prompt[];
  private lastStoredGPTConfigMap: Record<string, GptResponseData<T> | undefined>;
  constructor() {
    super();
    this.cachedConfigs = [];
    this.promptsList = [];
    this.lastStoredGPTConfigMap = {};
  }

  public getCachedData = (widgetId: string) =>
    !isNilOrEmpty(widgetId) ? this.cachedConfigs.filter(configs => configs.widgetId === widgetId) : this.cachedConfigs;

  public getCachedWithPromptData = (prompt: string, widgetId?: string): GptResponseData<T> | undefined => {
    const cachedConfig = this.cachedConfigs.find(d => d.prompt === prompt && d.widgetId === widgetId);
    if (!cachedConfig || !widgetId) return;

    const storedCompletions = this.lastStoredGPTConfigMap[widgetId]?.completions;

    return {
      completions: storedCompletions
        ? deepObjectMerge(storedCompletions, cachedConfig.completions)
        : cachedConfig.completions,
      prompt: cachedConfig.prompt,
    };
  };
  public updatePromptCachedData = (prompt: string, completions, widgetId?: string) => {
    if (!widgetId) return;
    const delta = this.lastStoredGPTConfigMap[widgetId]
      ? objectDeepDiff(completions, this.lastStoredGPTConfigMap[widgetId].completions)
      : completions;
    //store updated with last used config
    this.lastStoredGPTConfigMap[widgetId] = { completions, prompt };
    const index = this.cachedConfigs.findIndex(d => d.prompt === prompt);
    if (index !== -1) {
      this.cachedConfigs[index] = { completions: delta as T, prompt, widgetId };
    } else {
      this.cachedConfigs.push({ completions: delta as T, prompt, widgetId });
    }
    this.dispatch({ data: this.cachedConfigs, type: 'gpt-search:update' });
    return;
  };
  public updatePromptList = (promptList: Prompt[]) => (this.promptsList = promptList);
  public updateLastUsedConfig = (completions: T, prompt?: string, widgetId?: string) => {
    if (!widgetId) return;
    this.lastStoredGPTConfigMap[widgetId] = {
      completions,
      prompt: prompt ?? this.lastStoredGPTConfigMap[widgetId]?.prompt ?? '',
    };
  };
}
